import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _isNumber from "lodash/isNumber";
import _isNil from "lodash/isNil";
import _pipe from "lodash/fp/pipe";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

import { rgba } from 'polished';
import React, { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import styled, { css } from 'styled-components';
import { theme } from "../../theme";
import { darkScrollbarStyles } from "./scrollbar-styles";
import { mergeProps } from '@react-aria/utils';
import { getBackgroundColor } from "../../theme/color-tokens";
var DirectionIndices;

(function (DirectionIndices) {
  DirectionIndices[DirectionIndices["Top"] = 0] = "Top";
  DirectionIndices[DirectionIndices["Right"] = 1] = "Right";
  DirectionIndices[DirectionIndices["Bottom"] = 2] = "Bottom";
  DirectionIndices[DirectionIndices["Left"] = 3] = "Left";
})(DirectionIndices || (DirectionIndices = {}));

var StyledWrapperDiv = styled.div.withConfig({
  displayName: "ScrollContainer__StyledWrapperDiv",
  componentId: "vapor__sc-6kmj65-0"
})(["position:relative;flex:", ";display:flex;flex-direction:column;max-height:", ";min-height:", ";height:", ";"], function (p) {
  return p.$flex;
}, function (p) {
  return p.$maxHeight;
}, function (p) {
  return p.$minHeight;
}, function (p) {
  return p.$height;
});
var StyledDiv = styled.div.withConfig({
  displayName: "ScrollContainer__StyledDiv",
  componentId: "vapor__sc-6kmj65-1"
})(["height:100%;overflow-x:", ";overflow-y:", ";border:1px solid transparent;border-width:", "px ", "px;", " background-color:", ";", ";", ""], function (p) {
  return p.$x ? 'scroll' : 'hidden';
}, function (p) {
  return p.$y ? 'scroll' : 'hidden';
}, function (p) {
  return p.$y ? 1 : 0;
}, function (p) {
  return p.$x ? 1 : 0;
}, function (p) {
  return p.$hideBottomBorder && "border-bottom: 0;";
}, function (p) {
  return getBackgroundColor(p.$backgroundColor);
}, darkScrollbarStyles, function (p) {
  return _isNumber(p.$zIndex) && "z-index: ".concat(p.$zIndex, ";");
});
var StyledInnerDiv = styled.div.withConfig({
  displayName: "ScrollContainer__StyledInnerDiv",
  componentId: "vapor__sc-6kmj65-2"
})(["padding-left:", ";padding-right:", ";padding-top:", ";padding-bottom:", ";"], function (p) {
  return theme.spaces[p.$pX];
}, function (p) {
  return theme.spaces[p.$pX];
}, function (p) {
  return theme.spaces[p.$pY];
}, function (p) {
  return theme.spaces[p.$pY];
});
var shadowStyle = css(["background-image:linear-gradient( to bottom,", " 0%,", " 100% );"], rgba(theme.colors.palette.grey00, 0.3), rgba(theme.colors.palette.grey00, 0));
export var ShadowElement = styled.div.withConfig({
  displayName: "ScrollContainer__ShadowElement",
  componentId: "vapor__sc-6kmj65-3"
})(["position:absolute;left:0;width:100%;height:", ";pointer-events:none;background-repeat:no-repeat;background-position:top;background-size:100%;", ";border-top:", ";"], theme.spaces[24], function (p) {
  return p.$isShadowVisible ? shadowStyle : undefined;
}, function (p) {
  return p.$shouldDisplayTopBorder ? '1px solid transparent' : 'none';
});
export var ScrollContainer = forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
      topShadowRunwayHeight = _ref.topShadowRunwayHeight,
      _ref$enableBottomBord = _ref.enableBottomBorder,
      enableBottomBorder = _ref$enableBottomBord === void 0 ? true : _ref$enableBottomBord,
      _ref$enableShadow = _ref.enableShadow,
      enableShadow = _ref$enableShadow === void 0 ? true : _ref$enableShadow,
      _ref$shadowOffset = _ref.shadowOffset,
      shadowOffset = _ref$shadowOffset === void 0 ? '0' : _ref$shadowOffset,
      _ref$flex = _ref.flex,
      flex = _ref$flex === void 0 ? '1 auto' : _ref$flex,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? '100%' : _ref$height,
      _ref$maxHeight = _ref.maxHeight,
      maxHeight = _ref$maxHeight === void 0 ? 'none' : _ref$maxHeight,
      minHeight = _ref.minHeight,
      _ref$overflowX = _ref.overflowX,
      overflowX = _ref$overflowX === void 0 ? false : _ref$overflowX,
      _ref$overflowY = _ref.overflowY,
      overflowY = _ref$overflowY === void 0 ? true : _ref$overflowY,
      _ref$paddingX = _ref.paddingX,
      paddingX = _ref$paddingX === void 0 ? 16 : _ref$paddingX,
      _ref$paddingY = _ref.paddingY,
      paddingY = _ref$paddingY === void 0 ? 0 : _ref$paddingY,
      _ref$backgroundColor = _ref.backgroundColor,
      backgroundColor = _ref$backgroundColor === void 0 ? 'grey15' : _ref$backgroundColor,
      zIndex = _ref.zIndex,
      style = _ref.style,
      rest = _objectWithoutProperties(_ref, ["children", "topShadowRunwayHeight", "enableBottomBorder", "enableShadow", "shadowOffset", "flex", "height", "maxHeight", "minHeight", "overflowX", "overflowY", "paddingX", "paddingY", "backgroundColor", "zIndex", "style"]);

  var scrollRef = useRef();

  var _React$useState = React.useState([0, 1, 0, 0]),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      opacity = _React$useState2[0],
      setOpacity = _React$useState2[1];

  var onScroll = useCallback(function (evt) {
    var _evt$currentTarget = evt === null || evt === void 0 ? void 0 : evt.currentTarget,
        offsetHeight = _evt$currentTarget.offsetHeight,
        offsetWidth = _evt$currentTarget.offsetWidth,
        scrollHeight = _evt$currentTarget.scrollHeight,
        scrollLeft = _evt$currentTarget.scrollLeft,
        scrollTop = _evt$currentTarget.scrollTop,
        scrollWidth = _evt$currentTarget.scrollWidth;

    var _getAlphaValue = _pipe(function (x) {
      return Number.prototype.toPrecision.call(x, 2);
    }, function (x) {
      return Math.max(0, x);
    }, function (x) {
      return Math.min(1, x);
    });

    var scrollRate = 4;
    var topValue = _isNil(topShadowRunwayHeight) ? scrollTop / (scrollHeight / scrollRate) : scrollTop / topShadowRunwayHeight;

    var top = _getAlphaValue(topValue);

    var right = scrollLeft < scrollWidth - offsetWidth ? 1 : 0;
    var bottom = scrollTop < scrollHeight - offsetHeight ? 1 : 0;
    var left = scrollLeft ? 1 : 0;
    setOpacity([top, right, bottom, left]);
  }, [topShadowRunwayHeight]);

  var getBorderColor = function getBorderColor(opacityArr, directionName) {
    var opacityUnit = opacityArr[DirectionIndices[directionName]];
    var colorName = overflowY && opacityUnit !== 0 ? 'grey20' : 'transparent';
    return theme.colors.palette[colorName];
  };

  useLayoutEffect(function () {
    onScroll({
      currentTarget: scrollRef.current
    });
  }, [children, onScroll]);
  return /*#__PURE__*/React.createElement(StyledWrapperDiv, {
    $flex: flex,
    $height: height,
    $maxHeight: maxHeight,
    $minHeight: minHeight
  }, /*#__PURE__*/React.createElement(StyledDiv, _extends({
    $hideBottomBorder: !enableBottomBorder,
    $x: overflowX,
    $y: overflowY,
    $zIndex: zIndex
  }, mergeProps(rest, {
    onScroll: onScroll
  }), {
    $backgroundColor: backgroundColor,
    ref: mergeRefs([scrollRef, forwardedRef]),
    style: _objectSpread({
      borderRightColor: getBorderColor(opacity, DirectionIndices.Right),
      borderBottomColor: getBorderColor(opacity, DirectionIndices.Bottom),
      borderLeftColor: getBorderColor(opacity, DirectionIndices.Left)
    }, style)
  }), /*#__PURE__*/React.createElement(StyledInnerDiv, {
    $pX: paddingX,
    $pY: paddingY
  }, children), /*#__PURE__*/React.createElement(ShadowElement, {
    $isShadowVisible: enableShadow,
    $shouldDisplayTopBorder: shadowOffset === '0',
    style: {
      top: shadowOffset,
      opacity: opacity[0],
      borderTopColor: getBorderColor(opacity, DirectionIndices.Top)
    }
  })));
});